import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, ShieldCheck,
  CheckCircle2, AlertTriangle, Shield,
} from 'lucide-react';

import { roleApi }       from '@/api/role.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';

import type { RoleSummaryDto } from '@/types/role.types';

// ── Role List Page ─────────────────────────────────────────────────────────────

export const RoleListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canCreate = hasPermission(Permissions.ADMIN_ROLE_CREATE);
  const canEdit   = hasPermission(Permissions.ADMIN_ROLE_EDIT);

  const [items,   setItems]   = useState<RoleSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast]     = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await roleApi.getList();
      setItems(res.data?.data ?? []);
    } catch {
      showToast('Failed to load roles.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const systemRoles = items.filter(r => r.isSystemRole);
  const companyRoles = items.filter(r => !r.isSystemRole);

  const RoleRow = ({ role }: { role: RoleSummaryDto }) => (
    <tr className="hover:bg-blue-50 dark:hover:bg-slate-700/50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className={cn(
            'w-7 h-7 rounded-lg flex items-center justify-center shrink-0',
            role.isSystemRole
              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
          )}>
            <Shield size={14} />
          </div>
          <div>
            <p
              className={cn(
                'text-sm font-medium',
                canEdit && !role.isSystemRole
                  ? 'text-blue-700 dark:text-blue-400 cursor-pointer hover:underline'
                  : 'text-slate-900 dark:text-white',
              )}
              onClick={() => {
                if (canEdit && !role.isSystemRole)
                  navigate(`${AppRoutes.ADMIN_ROLES}/${role.id}`);
              }}
            >
              {role.name}
            </p>
            {role.description && (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 max-w-sm truncate">
                {role.description}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
        {role.code}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {role.isSystemRole && (
            <span className="px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full font-medium">
              System
            </span>
          )}
          <span className={cn(
            'px-2 py-0.5 text-xs rounded-full font-medium',
            role.isActive
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
          )}>
            {role.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        <span className="font-medium">{role.permissionCount}</span>
        <span className="text-slate-400 dark:text-gray-500 text-xs ml-1">permission{role.permissionCount !== 1 ? 's' : ''}</span>
      </td>
      <td className="px-4 py-3">
        {canEdit && !role.isSystemRole && (
          <button
            onClick={() => navigate(`${AppRoutes.ADMIN_ROLES}/${role.id}`)}
            className="px-2.5 py-1 text-xs font-medium rounded-lg
                       bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                       hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            Edit
          </button>
        )}
        {canEdit && role.isSystemRole && (
          <button
            onClick={() => navigate(`${AppRoutes.ADMIN_ROLES}/${role.id}`)}
            className="px-2.5 py-1 text-xs font-medium rounded-lg
                       bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300
                       hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            View
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2',
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* ── Header ───────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-blue-600" />
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Role Management</h1>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {items.length} role{items.length !== 1 ? 's' : ''} · {systemRoles.length} system, {companyRoles.length} company
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.ADMIN_ROLE_NEW)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium
                           rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={16} /> New Role
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {/* System Roles */}
            {systemRoles.length > 0 && (
              <div>
                <h2 className="text-xs font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">
                  System Roles
                </h2>
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Permissions</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {systemRoles.map(r => <RoleRow key={r.id} role={r} />)}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Company Roles */}
            <div>
              <h2 className="text-xs font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">
                Company Roles
              </h2>
              {companyRoles.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
                  <ShieldCheck size={40} className="mx-auto mb-3 opacity-20 text-slate-400" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">No company roles yet.</p>
                  {canCreate && (
                    <button
                      onClick={() => navigate(AppRoutes.ADMIN_ROLE_NEW)}
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-600
                                 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus size={14} /> Create Role
                    </button>
                  )}
                </div>
              ) : (
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Permissions</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {companyRoles.map(r => <RoleRow key={r.id} role={r} />)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
